import { useEffect } from 'react';
import { TOOLS } from '../constants/drawingTools';
import { recalculateShapeMeasurements, generateId, resolveShapeScaleProps } from '../utils/drawingUtils.js';
import { updateMultiPointShapeOnVertexDrag} from '../utils/booleanOperations.js';
import {
  DISPLAY_KEY_SEGMENT_LENGTH,
  DISPLAY_KEY_AREA,
  DISPLAY_KEY_MEASUREMENT,
} from '../shapes/ShapeRenderer';


export const useKeyboardShortcuts = (state, actions) => {
    const {
        selectedShapeIds,
        vertexEditInfo,
        originalVertexPosition,
        isDrawing,
        currentShape,
        selectedTool,
        lastActiveTool,
        currentScaleProps,
        snapIndicator,
        shapes,
        dpi = 72,
        imageSize = { width: 0, height: 0 },
        isOrthogonalModeActive,
        isRectangleMode,
        isSnappingEnabled,
        isGridVisible,
        strokeWidth,
        stageRef,
        displayKeys,
        displayUnitSystem,
        measurementSystem,
        displayKeysPanelVisible,
        allScales = [],
        insertVertexMode,
    } = state;

    const {
        setShapes,
        setSelectedShapeIds,
        setVertexEditInfo,
        setOriginalVertexPosition,
        setSnapIndicator,
        setVisibleSnapPoints,
        selectTool,
        setIsOrthogonalModeActive,
        finalizeAndResetDrawing,
        abortDrawing,
        setIsRectangleMode,
        setIsSnappingEnabled,
        setIsGridVisible,
        onCreateMeasurementSummary,
        handleStrokeWidthChange,
        setDisplayKeys,
        setDisplayUnitSystem,
        setDisplayKeysPanelVisible,
        setInsertVertexMode,
    } = actions;

    useEffect(() => {
        const handleKeyDown = (e) => {
            const targetTagName = e.target.tagName.toLowerCase();
            // ignore most form controls, but allow input[type="checkbox"] to receive shortcuts
            const targetType = (e.target.type || '').toLowerCase();
            if ((targetTagName === 'input' && targetType !== 'checkbox') || targetTagName === 'textarea' || targetTagName === 'select') {
                return;
            }

            // Defer to ContextualToolPanel when shapes are selected (it always handles R, C, V, F, Z;
            // U/X only when boolean ops apply, i.e. 2+ polygon shapes selected). Only defer UNMODIFIED
            // keys — Ctrl/Cmd combos (Ctrl+C/X/V clipboard) must reach the handlers below.
            const selectedPolygonCount = shapes.filter(
                (s) => selectedShapeIds.includes(s.id) && s.type === TOOLS.AREA,
            ).length;
            const showBooleanOps = selectedPolygonCount >= 2;
            const deferredKey = e.key.toLowerCase();
            const deferAlways = ['r', 'c', 'v', 'f', 'z'].includes(deferredKey);
            const deferWhenBooleanOps = showBooleanOps && ['u', 'x'].includes(deferredKey);
            if (selectedShapeIds.length > 0 && !e.ctrlKey && !e.metaKey && !e.altKey && (deferAlways || deferWhenBooleanOps)) {
                return;
            }

            // Delete/Backspace
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShapeIds.length > 0) {
                e.preventDefault();
                setShapes(prev => prev.filter(shape => !selectedShapeIds.includes(shape.id)));
                setSelectedShapeIds([]);
                return;
            }

            // Escape
            if (e.key === 'Escape' ) {
                if (insertVertexMode) {
                    setInsertVertexMode(false);
                    if (vertexEditInfo && vertexEditInfo.isInserted) {
                        setShapes(prevShapes =>
                            prevShapes.map(shape => {
                                if (shape.id === vertexEditInfo.shapeId) {
                                    const shapeScaleProps = resolveShapeScaleProps(vertexEditInfo.originalShape, currentScaleProps, allScales);
                                    return recalculateShapeMeasurements(vertexEditInfo.originalShape, shapeScaleProps, dpi, imageSize);
                                }
                                return shape;
                            })
                        );
                        setVertexEditInfo(null);
                        setOriginalVertexPosition(null);
                        if (snapIndicator) setSnapIndicator(null);
                        setVisibleSnapPoints([]);
                    } else if (vertexEditInfo && originalVertexPosition) {
                        setShapes(prevShapes =>
                            prevShapes.map(shape => {
                                if (shape.id === vertexEditInfo.shapeId &&
                                    (
                                        shape.type === TOOLS.POLYLINE || 
                                        shape.type === TOOLS.AREA ||
                                        shape.type === TOOLS.MEASURE ||
                                        shape.type === TOOLS.COTATION ||
                                        shape.type === TOOLS.ARROW
                                    )) {
                                    const updatedShape = updateMultiPointShapeOnVertexDrag(
                                        shape,
                                        vertexEditInfo.vertexIndex,
                                        originalVertexPosition.x,
                                        originalVertexPosition.y,
                                        vertexEditInfo.isHole || false,
                                        vertexEditInfo.holeIndex || 0
                                    );
                                    const shapeScaleProps = resolveShapeScaleProps(updatedShape, currentScaleProps, allScales);
                                    return recalculateShapeMeasurements(updatedShape, shapeScaleProps, dpi, imageSize);
                                }
                                return shape;
                            })
                        );
                        setVertexEditInfo(null);
                        setOriginalVertexPosition(null);
                        if (snapIndicator) setSnapIndicator(null);
                        setVisibleSnapPoints([]);
                    }
                    return;
                }
                if (isDrawing) {
                    abortDrawing();
                    selectTool(TOOLS.SELECT);
                    return;
                }
                if (selectedShapeIds.length > 0) {
                    setSelectedShapeIds([]);
                    return;
                }
                if (vertexEditInfo && originalVertexPosition) {
                    setShapes(prevShapes =>
                        prevShapes.map(shape => {
                            if (shape.id === vertexEditInfo.shapeId &&
                                (
                                    shape.type === TOOLS.POLYLINE || 
                                    shape.type === TOOLS.AREA ||
                                    shape.type === TOOLS.MEASURE ||
                                    shape.type === TOOLS.COTATION ||
                                    shape.type === TOOLS.ARROW
                                )) {
                                const updatedShape = updateMultiPointShapeOnVertexDrag(
                                    shape,
                                    vertexEditInfo.vertexIndex,
                                    originalVertexPosition.x,
                                    originalVertexPosition.y,
                                    vertexEditInfo.isHole || false,
                                    vertexEditInfo.holeIndex || 0
                                );
                                const shapeScaleProps = resolveShapeScaleProps(updatedShape, currentScaleProps, allScales);
                                return recalculateShapeMeasurements(updatedShape, shapeScaleProps, dpi, imageSize);
                            }
                            return shape;
                        })
                    );
                    setVertexEditInfo(null);
                    setOriginalVertexPosition(null);
                    if (snapIndicator) setSnapIndicator(null);
                    setVisibleSnapPoints([]);
                    return;
                }
                abortDrawing();
                selectTool(TOOLS.SELECT);
                return;
            }

            // 'q' for Stop / Quit active tool
            if (e.key.toLowerCase() === 'q' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(null);
                return;
            }

            // 'h' for Pen tool
            if (e.key.toLowerCase() === 'h' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.PEN);
                return;
            }

            // 'n' for Rectangle tool
            if (e.key.toLowerCase() === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.RECTANGLE);
                return;
            }

            // 't' for Text tool
            if (e.key.toLowerCase() === 't' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.TEXT);
                return;
            }

            // 'p' for Point tool
            if (e.key.toLowerCase() === 'p' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.POINT);
                return;
            }

            // 'l' for Polyline tool
            if (e.key.toLowerCase() === 'l' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.POLYLINE);
                return;
            }

            // 's' for Polygon tool
            if (e.key.toLowerCase() === 's' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.POLYGON);
                return;
            }

            // 'u' for Measurement Summary
            if (e.key.toLowerCase() === 'u' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (onCreateMeasurementSummary) onCreateMeasurementSummary();
                return;
            }

            // 'o' for Orthogonal Mode
            if (e.key.toLowerCase() === 'o' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setIsOrthogonalModeActive) {
                    setIsOrthogonalModeActive(prev => {
                        const newVal = !prev;
                        if (newVal && setIsRectangleMode) {
                            setIsRectangleMode(false);
                        }
                        return newVal;
                    });
                }
                return;
            }

            // 'r' for Rectangle Mode
            if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setIsRectangleMode) {
                    setIsRectangleMode(prev => {
                        const newVal = !prev;
                        if (newVal && setIsOrthogonalModeActive) {
                            setIsOrthogonalModeActive(false);
                        }
                        return newVal;
                    });
                }
                return;
            }

            // 'x' for Snapping Mode
            if (e.key.toLowerCase() === 'x' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setIsSnappingEnabled) setIsSnappingEnabled(prev => !prev);
                return;
            }

            // 'g' for Grid visibility
            if (e.key.toLowerCase() === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setIsGridVisible) setIsGridVisible(prev => !prev);
                return;
            }

            // 'm' for Toggle Metric / Imperial
            if (e.key.toLowerCase() === 'm' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setDisplayUnitSystem) {
                    const isMetricActive = displayUnitSystem === 'metric' || (displayUnitSystem === null && measurementSystem === 'metric');
                    setDisplayUnitSystem(isMetricActive ? 'imperial' : 'metric');
                }
                return;
            }

            // 'j' for Cycle measurements: Off -> Segments -> Total -> Off
            if (e.key.toLowerCase() === 'j' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setDisplayKeys && displayKeys) {
                    const hasSegmentLength = displayKeys.includes(DISPLAY_KEY_SEGMENT_LENGTH);
                    const hasTotalMeasurement = displayKeys.includes(DISPLAY_KEY_MEASUREMENT);
                    let newKeys = displayKeys.filter(
                        (k) => k !== DISPLAY_KEY_SEGMENT_LENGTH && k !== DISPLAY_KEY_MEASUREMENT
                    );

                    if (!hasSegmentLength && !hasTotalMeasurement) {
                        newKeys.push(DISPLAY_KEY_SEGMENT_LENGTH);
                    } else if (hasSegmentLength) {
                        newKeys.push(DISPLAY_KEY_MEASUREMENT);
                    }
                    setDisplayKeys(newKeys);
                }
                return;
            }

            // 'a' for Toggle Area Display
            if (e.key.toLowerCase() === 'a' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setDisplayKeys && displayKeys) {
                    const newKeys = displayKeys.includes(DISPLAY_KEY_AREA)
                        ? displayKeys.filter((k) => k !== DISPLAY_KEY_AREA)
                        : [...displayKeys, DISPLAY_KEY_AREA];
                    setDisplayKeys(newKeys);
                }
                return;
            }

            // 'd' for Toggle Display Settings Panel
            if (e.key.toLowerCase() === 'd' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (setDisplayKeysPanelVisible) {
                    setDisplayKeysPanelVisible(prev => !prev);
                }
                return;
            }

            // '-' for decreasing stroke width
            if (e.key === '-' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (handleStrokeWidthChange) {
                    handleStrokeWidthChange(Math.max(1, strokeWidth - 1));
                }
                return;
            }

            // '+' or '=' for increasing stroke width
            if ((e.key === '+' || e.key === '=') && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (handleStrokeWidthChange) {
                    handleStrokeWidthChange(Math.min(50, strokeWidth + 1));
                }
                return;
            }

            // 'k' for Cotation tool
            if (e.key.toLowerCase() === 'k' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.COTATION);
                return;
            }

            // 'w' for Arrow tool
            if (e.key.toLowerCase() === 'w' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                selectTool(TOOLS.ARROW);
                return;
            }

            // Spacebar
            if (e.code === 'Space' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                if (isDrawing && currentShape) {
                    finalizeAndResetDrawing();
                } else if (!isDrawing) {
                    if (selectedTool === TOOLS.SELECT) {
                        selectTool(lastActiveTool || TOOLS.POLYLINE);
                    } else {
                        selectTool(TOOLS.SELECT);
                    }
                }
                return;
            }

            // Ctrl+C (Copy)
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
                if (selectedShapeIds.length > 0) {
                    const copiedShapes = shapes
                        .filter(s => selectedShapeIds.includes(s.id))
                        .map(s => {
                            const cleaned = { ...s };
                            // Remove persistence metadata to avoid conflicts or stale data
                            delete cleaned.createdByUsername;
                            delete cleaned.createdAt;
                            delete cleaned.updatedByUsername;
                            delete cleaned.updatedAt;
                            return cleaned;
                        });
                    // Save source scale & DPI context for real-world scaling on paste
                    const clipboardPayload = {
                        shapes: copiedShapes,
                        sourceDpi: dpi,
                        sourceScaleProps: currentScaleProps,
                    };
                    localStorage.setItem('replan_clipboard_v2', JSON.stringify(clipboardPayload));
                }
                return;
            }

            // Ctrl+X (Cut)
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'x') {
                if (selectedShapeIds.length > 0) {
                    const shapesToCut = shapes
                        .filter(s => selectedShapeIds.includes(s.id))
                        .map(s => {
                            const cleaned = { ...s };
                            delete cleaned.createdByUsername;
                            delete cleaned.createdAt;
                            delete cleaned.updatedByUsername;
                            delete cleaned.updatedAt;
                            return cleaned;
                        });
                    const clipboardPayload = {
                        shapes: shapesToCut,
                        sourceDpi: dpi,
                        sourceScaleProps: currentScaleProps,
                    };
                    localStorage.setItem('replan_clipboard_v2', JSON.stringify(clipboardPayload));
                    
                    // Remove from current page
                    setShapes(prev => prev.filter(shape => !selectedShapeIds.includes(shape.id)));
                    setSelectedShapeIds([]);
                }
                return;
            }

            // Ctrl+V (Paste)
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
                const clipboardData = localStorage.getItem('replan_clipboard_v2') || localStorage.getItem('replan_clipboard');
                if (clipboardData) {
                    try {
                        const payload = JSON.parse(clipboardData);
                        let clipboardShapes = [];
                        let sourceDpi = dpi;
                        let sourceScaleProps = currentScaleProps;

                        // Support backward compatibility if older format clipboard exists
                        if (Array.isArray(payload)) {
                            clipboardShapes = payload;
                        } else if (payload && Array.isArray(payload.shapes)) {
                            clipboardShapes = payload.shapes;
                            sourceDpi = payload.sourceDpi || dpi;
                            sourceScaleProps = payload.sourceScaleProps || currentScaleProps;
                        }

                        if (clipboardShapes.length > 0) {
                            // Calculate scale scaling factor if shape scale is not locked
                            const sourceUnitFactor = (sourceScaleProps && sourceScaleProps.documentUnit === 'mm') ? 25.4 : 1;
                            const targetUnitFactor = (currentScaleProps && currentScaleProps.documentUnit === 'mm') ? 25.4 : 1;
                            
                            const sourceRatio = (sourceScaleProps && sourceScaleProps.ratio) ? sourceScaleProps.ratio : 1;
                            const targetRatio = (currentScaleProps && currentScaleProps.ratio) ? currentScaleProps.ratio : 1;

                            const scaleFactor = (dpi / sourceDpi) * 
                                               (sourceRatio / targetRatio) * 
                                               (sourceUnitFactor / targetUnitFactor);

                            // 1. Scale shape coordinates relative to their own values if unlocked
                            const scaledShapes = clipboardShapes.map(s => {
                                if (s.scaleKey) {
                                    // If scale is locked, keep the coordinates and size exactly as-is
                                    return { ...s };
                                }
                                
                                // Otherwise, scale coordinates to preserve real-world size
                                const updated = {
                                    ...s,
                                    x: s.x * scaleFactor,
                                    y: s.y * scaleFactor,
                                };
                                
                                if (updated.width !== undefined) updated.width *= scaleFactor;
                                if (updated.height !== undefined) updated.height *= scaleFactor;
                                if (updated.radius !== undefined) updated.radius *= scaleFactor;
                                
                                if (updated.points && Array.isArray(updated.points)) {
                                    updated.points = updated.points.map(p => p * scaleFactor);
                                }
                                
                                if (updated.holes && Array.isArray(updated.holes)) {
                                    updated.holes = updated.holes.map(hole => 
                                        Array.isArray(hole) ? hole.map(p => p * scaleFactor) : hole
                                    );
                                }

                                if (updated.centroid) {
                                    updated.centroid = {
                                        x: updated.centroid.x * scaleFactor,
                                        y: updated.centroid.y * scaleFactor,
                                    };
                                }
                                
                                return updated;
                            });

                            // 2. Align to mouse pointer position
                            let dx = 0;
                            let dy = 0;
                            if (stageRef && stageRef.current) {
                                const stage = stageRef.current;
                                const pointerPos = stage.getRelativePointerPosition();
                                if (pointerPos) {
                                    const firstShape = scaledShapes[0];
                                    dx = pointerPos.x - firstShape.x;
                                    dy = pointerPos.y - firstShape.y;
                                }
                            }

                            const newShapes = scaledShapes.map(s => ({
                                ...s,
                                id: generateId(),
                                x: s.x + dx,
                                y: s.y + dy,
                            }));

                            setShapes(prev => [...prev, ...newShapes]);
                            // Automatically select the newly pasted shapes
                            setSelectedShapeIds(newShapes.map(s => s.id));
                        }
                    } catch (err) {
                        console.error("Paste failed", err);
                    }
                }
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        selectedShapeIds, vertexEditInfo, originalVertexPosition, isDrawing, currentShape,
        selectedTool, lastActiveTool, currentScaleProps, snapIndicator, shapes, setShapes, setSelectedShapeIds,
        setVertexEditInfo, setOriginalVertexPosition, setSnapIndicator, selectTool,
        setIsOrthogonalModeActive, finalizeAndResetDrawing,
        isOrthogonalModeActive, isRectangleMode, isSnappingEnabled, isGridVisible, strokeWidth,
        setIsRectangleMode, setIsSnappingEnabled, setIsGridVisible, onCreateMeasurementSummary, handleStrokeWidthChange,
        stageRef,
        displayKeys, displayUnitSystem, measurementSystem, displayKeysPanelVisible,
        setDisplayKeys, setDisplayUnitSystem, setDisplayKeysPanelVisible,
        insertVertexMode, setInsertVertexMode
    ]);
};